import { noop, round } from "lodash";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import { Payment } from "types";
import { CashCalculatorModal } from "./CashCalculatorModal";
import { PaymentInputRef } from "./PaymentInputRef";

interface CashInputProps {
    amount?: number;
    value: Payment;
}

interface CashCalculatorState {
    isOpen: boolean;
    minAmount: number;
    resolve: (payment?: Payment) => void;
}

export const CashInput = forwardRef<PaymentInputRef, CashInputProps>(({ value, amount = 0 }, ref) => {
    const [cashCalculatorState, setCashCalculatorState] = useState<CashCalculatorState>({
        isOpen: false,
        minAmount: amount ? Math.abs(amount) : 0,
        resolve: noop,
    });

    useImperativeHandle(ref, () => ({
        async prepare() {
            return new Promise((resolve) => {
                setCashCalculatorState({
                    isOpen: true,
                    minAmount: amount ? Math.abs(amount) : 0,
                    resolve: (payment?: Payment) => {
                        resolve(payment);
                    },
                });
            });
        },

        async tokenize() {
            return null;
        },

        // Dummy function won't be used for cash payments
        async confirm() {
            return true;
        },
    }));

    const handleCloseCashCalculator = (payment?: Payment) => () => {
        cashCalculatorState.resolve(payment);
        setCashCalculatorState({ isOpen: false, minAmount: amount ?? 0, resolve: noop });
    };

    const handleCashCalculatorSubmit = (paidAmount: number) => {
        const purchaseAmount = cashCalculatorState.minAmount;
        if (paidAmount > purchaseAmount) {
            const change = round(paidAmount - Math.abs(purchaseAmount), 2);
            const payment = {
                ...value,
                meta: { cashCalculator: { amount: purchaseAmount, cash: paidAmount, change: change } },
            };
            handleCloseCashCalculator(payment)();
        } else {
            handleCloseCashCalculator(value)();
        }
    };

    return (
        <CashCalculatorModal
            minAmount={cashCalculatorState.minAmount}
            isOpen={cashCalculatorState.isOpen}
            onClose={handleCloseCashCalculator()}
            onSubmit={handleCashCalculatorSubmit}
        />
    );
});
